import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/comics";
import { formatCount, getComicById } from "@/utils/comics";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setActive((index) => (index + 1) % heroSlides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const slide = heroSlides[active];
  const comic = getComicById(slide.comicId);
  const goPrev = () => setActive((index) => (index - 1 + heroSlides.length) % heroSlides.length);
  const goNext = () => setActive((index) => (index + 1) % heroSlides.length);

  return (
    <section aria-label="精选推荐轮播" className="relative">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <div className="mb-5 flex flex-col gap-3 md:mb-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
              JMComic · 精选漫画阅读平台
            </span>
            <h1 className="mt-2 font-heading text-[26px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              JMComic漫画频道
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-500">
              聚合 8 大题材的精选漫画资源，按分类轻松浏览，追踪每日连载更新。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-600">
              <strong className="font-heading text-sm text-foreground-950">20</strong> 部精选作品
            </span>
            <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-600">
              <strong className="font-heading text-sm text-foreground-950">8</strong> 大题材
            </span>
            <span className="rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-800">
              每日更新
            </span>
          </div>
        </div>

        <div
          className="relative h-[460px] overflow-hidden rounded-lg border border-background-200 bg-secondary-900 md:h-[460px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <img
            key={slide.image}
            src={slide.image}
            alt={`${slide.title}推荐封面图`}
            title={`${slide.title} - JMComic精选推荐`}
            className="animate-fade absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent"></div>

          <div className="relative flex h-full flex-col justify-end p-5 pb-16 md:justify-center md:p-10 md:pb-10">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-[11px] font-semibold text-background-50">
                <i className="ri-fire-line"></i>
                {slide.subtitle}
              </span>
              <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
                {slide.title}
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
                {slide.description}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-background-300 md:text-xs">
                {comic && (
                  <>
                    <span>{comic.author}</span>
                    <span className="h-1 w-1 rounded-full bg-background-500"></span>
                    <span>{comic.categoryName}</span>
                    <span className="h-1 w-1 rounded-full bg-background-500"></span>
                    <span>{comic.region}</span>
                    <span className="h-1 w-1 rounded-full bg-background-500"></span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatCount(comic.viewCount)}
                    </span>
                  </>
                )}
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <Link
                  to={`/detail/${slide.comicId}`}
                  className="flex h-11 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看漫画详情
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/updates"
                  className="flex h-11 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-400/60 bg-background-50/10 px-5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-flashlight-line"></i>
                  今日更新
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 left-5 flex items-center gap-1.5 md:bottom-6 md:left-10">
            {heroSlides.map((item, index) => (
              <button
                key={item.comicId}
                type="button"
                aria-label={`查看第${index + 1}张推荐`}
                onClick={() => setActive(index)}
                className={`h-1.5 rounded-full transition-all ${
                  index === active ? "w-8 bg-primary-400" : "w-3 bg-background-50/40 hover:bg-background-50/70"
                }`}
              ></button>
            ))}
          </div>

          <div className="absolute bottom-4 right-4 flex gap-2 md:bottom-6 md:right-6">
            <button
              type="button"
              onClick={goPrev}
              aria-label="上一张"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur transition-colors hover:bg-primary-500"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="下一张"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur transition-colors hover:bg-primary-500"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}